import { useEffect, useRef, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import type { AppAccessSystem } from '../../services/api/appAccess';

export interface RevokeConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (confirmation: string) => void;
  /** @deprecated — kept for API compatibility; all revokes now use dispatch mode. */
  onManualConfirm?: () => void;
  /** @deprecated — always treated as 'dispatch'; manual mode removed with dyq dead-code cleanup. */
  mode?: 'dispatch' | 'manual';
  user: { firstName: string | null; lastName: string | null; email: string };
  system: AppAccessSystem;
  systemDisplayName: string;
  isLoading: boolean;
  serverError: string | null;
}

export function RevokeConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  user,
  systemDisplayName,
  isLoading,
  serverError,
}: RevokeConfirmModalProps) {
  const [confirmationInput, setConfirmationInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset value when modal opens fresh
  useEffect(() => {
    if (isOpen) {
      setConfirmationInput('');
    }
  }, [isOpen]);

  // Defense-in-depth gate (T-09-01) — case-sensitive trim parity with Phase 8 D-06.
  const isConfirmEnabled = confirmationInput.trim() === user.email && !isLoading;

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;
    onConfirm(confirmationInput.trim());
  };

  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email;
  const firstName = user.firstName ?? user.email;

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      ariaLabelledBy="revoke-modal-title"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Keep access
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled}
            isLoading={isLoading}
          >
            Confirm removal
          </Button>
        </div>
      }
    >
      <h2 id="revoke-modal-title" className="text-lg font-semibold text-gray-900">
        Revoke {systemDisplayName} access for {fullName}?
      </h2>

      <p className="text-sm text-gray-600 mt-3">
        This will dispatch an offboarding workflow that removes {firstName}&apos;s access to{' '}
        {systemDisplayName}. The action cannot be undone automatically — you must grant access
        again to restore it.
      </p>
      <label
        htmlFor="revoke-confirmation-input"
        className="block text-sm font-semibold text-gray-700 mt-4 mb-1"
      >
        Type {user.email} to confirm
      </label>
      <input
        ref={inputRef}
        id="revoke-confirmation-input"
        type="email"
        autoComplete="off"
        autoFocus
        value={confirmationInput}
        onChange={(e) => setConfirmationInput(e.target.value)}
        placeholder={user.email}
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        aria-describedby="revoke-confirm-hint"
      />
      <p id="revoke-confirm-hint" className="text-xs text-gray-400 mt-1">
        Case-sensitive. Must match exactly.
      </p>
      {serverError && <p className="mt-2 text-sm text-red-600">{serverError}</p>}
    </Modal>
  );
}
